import React from 'react';
import EditNoteIcon from '../../../img/edit-icon.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { SavedDeliveryPickupPayload } from '@/app/models/checkout/types';
import InfoIcon from '@mui/icons-material/Info';

type Props = {
    payload: SavedDeliveryPickupPayload | null;
    hasSpecialOrderItems: boolean;
    onEdit: () => void;
    isEagleViewLocked?: boolean;
};

export const PickupDeliverySaved: React.FC<Props> = ({ payload, hasSpecialOrderItems, onEdit, isEagleViewLocked }) => {
    const hasHours = payload?.pickupLocation?.openingHours && payload?.pickupLocation?.openingHours.length > 0;

    return (payload &&
        <Box
            className="pickup-delivery-saved"
            sx={{
                backgroundColor: 'background.paper',
                borderRadius: 2,
            }}
        >
            <Box className="checkout-container-header" sx={{ mb: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CheckCircleIcon sx={{ color: "#64A70B", padding: "1px", fontSize: "36px", mr: 1, }} />
                        <Typography className='checkout-container-title'>
                            {payload.isPickup ? 'Pickup' : 'Delivery'}
                        </Typography>
                        <Divider className="checkout-divider" />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ border: '10px solid', borderColor: 'grey.300', borderRadius: 2, p: 3 }}>
                <Box className="pickup-content checkout-container-content">
                    <Box className="select-pickup-points" mb={2}>
                        <Box className="single-pickup-point">
                            <Box
                                className="single-pickup-point-header"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography
                                    id="pickup-delivery-title"
                                    sx={{ fontSize: '1.25rem', fontWeight: 600 }}
                                >
                                    {payload.isPickup ? 'Pickup Location' : 'Delivery Location'}:
                                </Typography>
                                {!isEagleViewLocked && !payload?.isEagleViewDelivery && (
                                    <Button
                                        id="pickup-delivery-saved-title"
                                        type="button"
                                        onClick={onEdit}
                                        startIcon={<img src={EditNoteIcon} alt="" />}
                                        sx={{ fontWeight: 700, color: "#003B5C", textTransform: "none" }}>
                                        Edit
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {payload.isPickup ?
                        (<Box>
                            {payload.pickupLocation && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography fontWeight="bold" sx={{ mr: 1 }}>
                                            {payload.pickupLocation.name}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {hasHours && (
                                                <Tooltip
                                                    title={
                                                        <Box display="flex" flexDirection="column">
                                                            {payload.pickupLocation.openingHours?.map((hour, index) => (
                                                                <Typography
                                                                    key={index}
                                                                    variant="caption"
                                                                    sx={{ fontSize: "10px", color: "white" }}
                                                                >
                                                                    {hour}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    }
                                                    placement="top"
                                                    arrow >
                                                    <InfoIcon
                                                        sx={{
                                                            fontSize: 20,
                                                            color: "primary.main",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Typography className="primary-branch">
                                                Primary Branch
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography>{payload.pickupLocation.address}</Typography>
                                    <Typography>{payload.pickupLocation.cityStateZip}</Typography>
                                    <Typography>{payload.pickupLocation.phone}</Typography>
                                    {payload.pickupLocation.pickupFee && (
                                        <Typography>{payload.pickupLocation.pickupFee}</Typography>
                                    )}
                                </>
                            )}
                        </Box>)
                        :
                        (<Box>
                            {payload.deliveryAddress && (
                                <>
                                    <Typography fontWeight="bold">
                                        {payload.deliveryAddress.firstName} {payload.deliveryAddress.lastName} {payload.deliveryAddress.company}
                                    </Typography>
                                    <Typography>
                                        {payload.deliveryAddress.address1}
                                        {payload.deliveryAddress.address2 &&
                                            `, ${payload.deliveryAddress.address2}`}
                                    </Typography>
                                    <Typography>
                                        {payload.deliveryAddress.city}, {payload.deliveryAddress.stateName}{" "}
                                        {payload.deliveryAddress.zip}
                                    </Typography>
                                    <Typography>
                                        {(payload.deliveryAddress.phone || "")
                                            .replace(/\D/g, "")
                                            .replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")}
                                    </Typography>
                                </>
                            )}
                        </Box>)
                    }

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <Typography className="bold-text-field" fontWeight="bold">
                                Needed By
                            </Typography>
                            <Typography id="delivery-pickup-time-id">
                                {payload.selectedDate &&
                                    `${payload.selectedDate} ${payload.selectedTime}`}
                            </Typography>
                        </Grid>

                        {payload.note && (
                            <Grid item xs={12}>
                                <Typography className="bold-text-field" fontWeight="bold" mt={1}>
                                    Order Notes
                                </Typography>
                                <Typography id="order-note-id"
                                    sx={{
                                        overflowWrap: 'anywhere',     
                                        wordBreak: 'break-word', 
                                        whiteSpace: 'pre-wrap', 
                                        maxWidth: '100%',
                                    }}>{payload.note}</Typography>
                            </Grid>
                        )}
                    </Grid>

                    {hasSpecialOrderItems && (
                        <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                style={{ width: 30, flexShrink: 0 }}
                            >
                                <path
                                    fill="#B86125"
                                    d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
                                />
                            </svg>

                            <Typography sx={{ color: "#B86125", ml: 1 }}>
                                This order contains special order items. After you place your order, an
                                associate will contact you regarding the first available date.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
